import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import type { CookieOptions, AdvCookieOptions, CookieRef } from '@/utils/type'

// 全局 cookie 缓存，用于服务端渲染时共享实例
const globalCookieCache = new Map<string, any>()

// 检查是否在浏览器环境
const isClient = typeof window !== 'undefined'

// 解析 cookie 字符串
function parseCookies(cookieString: string): Record<string, string> {
  const cookies: Record<string, string> = {}
  if (!cookieString) return cookies

  cookieString.split(';').forEach((cookie) => {
    const [name, value] = cookie.trim().split('=')
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value)
    }
  })
  return cookies
}

// 序列化 cookie 选项
function serializeCookieOptions(options: CookieOptions): string {
  const parts: string[] = []

  if (options.path) parts.push(`path=${options.path}`)
  if (options.domain) parts.push(`domain=${options.domain}`)
  if (options.maxAge) parts.push(`max-age=${options.maxAge}`)
  if (options.expires) parts.push(`expires=${options.expires.toUTCString()}`)
  if (options.secure) parts.push('secure')
  if (options.httpOnly) parts.push('httponly')
  if (options.sameSite) parts.push(`samesite=${options.sameSite}`)

  return parts.join('; ')
}

// 设置 cookie
function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (!isClient) return

  const cookieValue = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
  const cookieOptions = serializeCookieOptions(options)
  const fullCookie = cookieOptions ? `${cookieValue}; ${cookieOptions}` : cookieValue

  document.cookie = fullCookie
}

// 获取 cookie
function getCookie(name: string): string | null {
  if (!isClient) return null

  const cookies = parseCookies(document.cookie)
  return cookies[name] || null
}

// 删除 cookie
function deleteCookie(name: string, options: CookieOptions = {}): void {
  if (!isClient) return

  setCookie(name, '', {
    ...options,
    maxAge: -1,
    expires: new Date(0)
  })
}

export function useCookieAdv<T = string>(
  name: string,
  options: AdvCookieOptions<T> = {}
): CookieRef<T> {
  // 设置默认过期时间（1年）
  if (!options.maxAge && !options.expires) {
    options.maxAge = 3600 * 24 * 365
  }

  // 检查全局缓存
  const cacheKey = `${name}_${JSON.stringify(options)}`
  if (globalCookieCache.has(cacheKey)) {
    return globalCookieCache.get(cacheKey)
  }

  // 默认值处理
  const defaultValue = options.default
    ? typeof options.default === 'function'
      ? options.default()
      : options.default
    : null

  // 创建响应式引用
  const cookieValue = ref(defaultValue)

  // 编码/解码函数
  const encode =
    options.encode ||
    ((value: T) => {
      if (typeof value === 'string') return value
      return JSON.stringify(value)
    })

  const decode =
    options.decode ||
    ((value: string) => {
      try {
        return JSON.parse(value) as T
      } catch {
        return value as T
      }
    })

  // 读取 cookie 值
  const readCookie = () => {
    if (!isClient) return

    const rawValue = getCookie(name)
    if (rawValue !== null) {
      try {
        cookieValue.value = decode(rawValue)
      } catch (error) {
        console.warn(`Failed to decode cookie "${name}":`, error)
      }
    }
  }

  // 写入 cookie 值
  const writeCookie = (value) => {
    if (!isClient) return

    if (value === null || value === undefined) {
      deleteCookie(name, options as CookieOptions)
    } else {
      const encodedValue = encode(value)
      setCookie(name, encodedValue, options as CookieOptions)
    }
  }

  // 监听值变化
  const stopWatch = watch(
    cookieValue,
    (newValue) => {
      writeCookie(newValue)
    },
    { deep: options.watch === true }
  )

  // 初始化时读取 cookie
  if (isClient) {
    readCookie()
  }

  // 监听 storage 事件（跨标签页同步）
  let storageHandler: ((e: StorageEvent) => void) | null = null

  if (isClient && options.watch !== false) {
    storageHandler = (e: StorageEvent) => {
      if (e.key === `cookie_${name}`) {
        readCookie()
      }
    }
    window.addEventListener('storage', storageHandler)
  }

  // 清理函数
  const cleanup = () => {
    stopWatch()
    if (storageHandler) {
      window.removeEventListener('storage', storageHandler)
    }
  }

  // 组件卸载时清理
  const instance = getCurrentInstance()
  if (instance) {
    onUnmounted(cleanup)
  }

  // 创建 cookie 引用对象
  const cookieRef: CookieRef<T> = computed({
    get: () => cookieValue.value as T,
    set: (value: T) => {
      cookieValue.value = value
    }
  })

  // 添加额外方法
  Object.defineProperty(cookieRef, 'remove', {
    value: () => {
      cookieValue.value = null as T
      deleteCookie(name, options as CookieOptions)
    }
  })

  // 缓存到全局
  globalCookieCache.set(cacheKey, cookieRef)

  return cookieRef
}
